import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../api/axios';
import { C } from '../theme';

const STATUS_STYLE = {
  'in-stock':    { bg: '#DCFCE7', color: '#15803D', label: 'In Stock',     icon: '📦' },
  'dealer-stock':{ bg: '#F0FDFA', color: '#0F766E', label: 'Dealer Stock', icon: '🏪' },
  'packed':      { bg: '#EDE9FE', color: '#6D28D9', label: 'Packed',       icon: '🎁' },
  'dispatched':  { bg: '#DBEAFE', color: '#1D4ED8', label: 'Dispatched',   icon: '🚚' },
  'installed':   { bg: '#D1FAE5', color: '#065F46', label: 'Installed',    icon: '✅' },
  'replacement': { bg: '#E0F2FE', color: '#0369A1', label: 'Replacement',  icon: '🔄' },
  'cancelled':   { bg: '#FEE2E2', color: '#991B1B', label: 'Cancelled',    icon: '🚫' },
  'damaged':     { bg: '#FEF3C7', color: '#92400E', label: 'Damaged',      icon: '⚠️' },
};

const RENEW = {
  overdue:  { bg: '#FEE2E2', color: '#991B1B', bar: '#EF4444', label: 'Overdue'  },
  due_soon: { bg: '#FEF3C7', color: '#92400E', bar: '#F59E0B', label: 'Due Soon' },
  upcoming: { bg: '#D1FAE5', color: '#065F46', bar: '#10B981', label: 'Upcoming' },
};

const WARRANTY = {
  active:   { bg: '#D1FAE5', color: '#065F46', label: 'Under warranty' },
  expiring: { bg: '#FEF3C7', color: '#92400E', label: 'Warranty ending' },
  expired:  { bg: '#FEE2E2', color: '#991B1B', label: 'Warranty expired' },
};

const SUPPORT = {
  open:          { bg: '#FEE2E2', color: '#991B1B', label: 'Open' },
  'in-progress': { bg: '#FEF3C7', color: '#92400E', label: 'In Progress' },
  resolved:      { bg: '#D1FAE5', color: '#065F46', label: 'Resolved' },
  closed:        { bg: '#F3F4F6', color: '#374151', label: 'Closed' },
};

const money = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;
const daysText = (d) =>
  d < 0 ? `${Math.abs(d)} days overdue` : d === 0 ? 'Due today' : `in ${d} days`;

export default function ClientProfileDrawer({ clientId, onClose }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState('overview');
  const [closing, setClosing] = useState(false);
  const [devQ,    setDevQ]    = useState('');

  const close = useCallback(() => {
    setClosing(true);
    setTimeout(onClose, 220);
  }, [onClose]);

  useEffect(() => {
    if (!clientId) return;
    let alive = true;
    setLoading(true); setTab('overview'); setDevQ('');
    (async () => {
      try {
        const r = await api.get(`/api/clients/${clientId}/profile`);
        if (alive) setData(r.data);
      } catch { if (alive) setData(null); }
      finally  { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [clientId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  const devices = useMemo(() => {
    if (!data) return [];
    const q = devQ.trim().toLowerCase();
    if (!q) return data.devices;
    return data.devices.filter(d =>
      [d.serial_number, d.ref_number, d.item_id, d.category_name, d.type_name]
        .some(v => v?.toLowerCase?.().includes(q))
    );
  }, [data, devQ]);

  if (!clientId) return null;

  const c = data?.client;
  const s = data?.summary;

  const TABS = data ? [
    { key: 'overview', label: 'Overview', icon: '📋' },
    { key: 'devices',  label: 'Devices',  icon: '📟', count: data.devices.length },
    { key: 'renewals', label: 'Renewals', icon: '🔁', count: data.renewals.length },
    { key: 'payments', label: 'Payments', icon: '💳', count: data.payments.length },
    { key: 'orders',   label: 'Orders',   icon: '🧾', count: data.orders.length },
    { key: 'support',  label: 'Support',  icon: '🛠️', count: data.support.length },
  ] : [];

  return (
    <>
      <style>{`
        @keyframes drawerUp   { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes drawerDown { from { transform: translateY(0); } to { transform: translateY(100%); } }
        @keyframes fadeIn     { from { opacity: 0; } to { opacity: 1; } }
        .cpd-scroll::-webkit-scrollbar { width: 9px; }
        .cpd-scroll::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 8px; }
        .cpd-row:hover { background: ${C.primaryBg} !important; }
      `}</style>

      <div onClick={close} style={{
        position: 'fixed', inset: 0, background: 'rgba(14,10,3,0.58)', zIndex: 3000,
        backdropFilter: 'blur(2px)', animation: 'fadeIn 0.2s ease',
        opacity: closing ? 0 : 1, transition: 'opacity 0.2s',
      }} />

      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 3001,
        height: '92vh', background: '#FCFCFD',
        borderRadius: '22px 22px 0 0', boxShadow: '0 -14px 60px rgba(0,0,0,0.3)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        animation: `${closing ? 'drawerDown' : 'drawerUp'} 0.26s cubic-bezier(0.32,0.72,0,1)`,
      }}>

        <div onClick={close} style={{ padding: '9px 0 3px', display: 'flex', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, background: C.white }}>
          <div style={{ width: '46px', height: '4px', borderRadius: '4px', background: '#D1D5DB' }} />
        </div>

        {loading ? (
          <Center><Spinner /><div style={{ color: C.textSub, fontSize: '13px', marginTop: '12px' }}>Loading client profile…</div></Center>
        ) : !data ? (
          <Center>
            <div style={{ fontSize: '36px' }}>⚠️</div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: C.textMain, margin: '8px 0 12px' }}>Could not load this client</div>
            <button onClick={close} style={ghostBtn}>Close</button>
          </Center>
        ) : (
          <>
            {/* ══ HEADER ══ */}
            <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
              <div style={{ padding: '8px 28px 0', display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{
                  width: '58px', height: '58px', borderRadius: '16px', flexShrink: 0,
                  background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '23px', fontWeight: 800, color: '#fff',
                  boxShadow: '0 4px 14px rgba(245,166,35,0.35)',
                }}>
                  {(c.client_name || '?')[0].toUpperCase()}
                </div>

                <div style={{ flex: 1, minWidth: '240px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '21px', fontWeight: 800, color: C.textMain }}>{c.client_name}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: C.primaryDeep, background: C.primaryBg, padding: '3px 9px', borderRadius: '6px' }}>
                      {c.client_id}
                    </span>
                    <Pill bg={c.status === 'active' ? C.successBg : C.dangerBg} color={c.status === 'active' ? C.successText : C.dangerText}>
                      {c.status === 'active' ? '● Active' : '○ Inactive'}
                    </Pill>
                    {c.dealer_name && <Pill bg="#F0FDFA" color="#0F766E">🏪 {c.dealer_name}</Pill>}
                    {c.purpose === 'company' && <Pill bg={C.warningBg} color={C.warningText}>🏢 Company</Pill>}
                    {s.overdue_count > 0 && <Pill bg="#FEE2E2" color="#991B1B">🚨 {s.overdue_count} overdue renewal{s.overdue_count > 1 ? 's' : ''}</Pill>}
                    {s.open_support > 0 && <Pill bg="#FEF3C7" color="#92400E">🛠️ {s.open_support} open ticket{s.open_support > 1 ? 's' : ''}</Pill>}
                  </div>

                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '7px' }}>
                    <Meta icon="📞" value={c.contact} copy />
                    {c.email   && <Meta icon="✉️" value={c.email} copy />}
                    {c.address && <Meta icon="📍" value={c.address} />}
                    {c.company_name && <Meta icon="🏢" value={c.company_name} />}
                    {c.gst_number   && <Meta icon="🧾" value={`GST ${c.gst_number}`} copy />}
                    {c.created_at   && <Meta icon="📅" value={`Since ${c.created_at}`} />}
                  </div>
                </div>

                <button onClick={close} title="Close (Esc)" style={{
                  background: '#F3F4F6', border: 'none', width: '32px', height: '32px', borderRadius: '9px',
                  fontSize: '15px', cursor: 'pointer', color: C.textSub, flexShrink: 0,
                }}>✕</button>
              </div>

              {/* Tabs */}
              <div style={{ padding: '14px 28px 0', display: 'flex', gap: '2px', overflowX: 'auto' }}>
                {TABS.map(t => {
                  const active = tab === t.key;
                  return (
                    <button key={t.key} onClick={() => setTab(t.key)} style={{
                      padding: '9px 15px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700,
                      background: 'transparent', color: active ? C.primaryDeep : C.textSub,
                      borderBottom: `3px solid ${active ? C.primary : 'transparent'}`,
                      marginBottom: '-1px', whiteSpace: 'nowrap',
                      display: 'flex', alignItems: 'center', gap: '6px',
                    }}>
                      <span>{t.icon}</span>{t.label}
                      {t.count > 0 && (
                        <span style={{
                          padding: '1px 7px', borderRadius: '20px', fontSize: '10px', fontWeight: 800,
                          background: active ? C.primary : '#E5E7EB', color: active ? '#fff' : C.textSub,
                        }}>{t.count}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ══ BODY ══ */}
            <div className="cpd-scroll" style={{ flex: 1, overflowY: 'auto', padding: '20px 28px 36px' }}>

              {/* ── OVERVIEW ── */}
              {tab === 'overview' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(260px,1fr)', gap: '18px', alignItems: 'start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>

                    {/* Money */}
                    <Panel title="Billing" right={
                      <Pill bg={s.balance_due > 0.01 ? C.dangerBg : C.successBg} color={s.balance_due > 0.01 ? C.dangerText : C.successText}>
                        {s.balance_due > 0.01 ? `${money(s.balance_due)} outstanding` : '✓ Fully settled'}
                      </Pill>
                    }>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                        <span style={{ fontSize: '26px', fontWeight: 900, color: C.successText }}>{money(s.total_paid)}</span>
                        <span style={{ fontSize: '13px', color: C.textSub }}>of {money(s.total_billed)} billed</span>
                      </div>
                      <Bar pct={s.paid_percent} color={s.balance_due > 0.01 ? C.warning : C.success} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 800, color: C.textSub }}>{s.paid_percent}% collected</span>
                        <span style={{ fontSize: '11px', color: C.textSub }}>{s.payment_count} payment{s.payment_count !== 1 ? 's' : ''}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '14px' }}>
                        <MiniStat label="Product value" value={money(s.product_total)} sub={`${s.order_count} order${s.order_count !== 1 ? 's' : ''}`} />
                        <MiniStat label="Renewals billed" value={money(s.renewal_total)} sub={`${s.renewal_count} renewal${s.renewal_count !== 1 ? 's' : ''}`} />
                      </div>
                    </Panel>

                    {/* Upcoming renewals */}
                    <Panel title="Upcoming Renewals" right={<span style={{ fontSize: '11px', color: C.textSub }}>{s.renewable_count} renewable device{s.renewable_count !== 1 ? 's' : ''}</span>}>
                      {(() => {
                        const up = data.devices
                          .filter(d => d.is_renewable && !d.cancelled)
                          .sort((a, b) => a.days_remaining - b.days_remaining);
                        if (up.length === 0) return <SmallEmpty text="No renewable devices for this client." />;
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {up.map(d => {
                              const r = RENEW[d.renewal_status] || RENEW.upcoming;
                              return (
                                <div key={`${d.mapping_db_id}-${d.inv_db_id}`} style={{ border: `1px solid ${C.border}`, borderRadius: '10px', padding: '11px 13px', background: C.white }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontSize: '13px', fontWeight: 700, color: C.textMain }}>
                                        {d.category_name} {d.type_name && <span style={{ color: C.textSub, fontWeight: 600 }}>· {d.type_name}</span>}
                                      </div>
                                      <div style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textSub }}>{d.serial_number}</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                      <div style={{ fontSize: '13px', fontWeight: 800, color: r.color }}>{d.due_date}</div>
                                      <div style={{ fontSize: '11px', color: C.textSub }}>{daysText(d.days_remaining)}</div>
                                    </div>
                                  </div>
                                  <div style={{ marginTop: '8px' }}><Bar pct={d.term_progress} color={r.bar} /></div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px' }}>
                                    <Pill bg={r.bg} color={r.color}>{r.label}</Pill>
                                    <span style={{ fontSize: '11px', fontWeight: 800, color: '#7C3AED' }}>AMC {money(d.amc_amount)}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </Panel>

                    {/* Vehicles */}
                    {data.vehicles.length > 0 && (
                      <Panel title="Vehicles">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '10px' }}>
                          {data.vehicles.map(v => (
                            <div key={v.id} style={{ border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 14px', background: C.white }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '18px' }}>🚗</span>
                                <span style={{ fontFamily: 'monospace', fontSize: '10px', color: C.textSub }}>{v.app_id}</span>
                              </div>
                              <div style={{ fontSize: '13px', fontWeight: 800, color: C.textMain, marginTop: '6px' }}>
                                {[v.make, v.model].filter(Boolean).join(' ') || v.vehicle_type}
                              </div>
                              <div style={{ fontSize: '11px', color: C.textSub }}>{v.vehicle_type}</div>
                              {v.registration_number && (
                                <div style={{ marginTop: '7px', fontFamily: 'monospace', fontSize: '12px', fontWeight: 800, color: C.textMain, background: C.primaryBg, padding: '3px 9px', borderRadius: '6px', display: 'inline-block' }}>
                                  {v.registration_number}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </Panel>
                    )}
                  </div>

                  {/* Right rail */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <Panel title="At a Glance">
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <Tile v={s.device_count}    l="Devices"    bg="#EFF6FF" c="#1D4ED8" />
                        <Tile v={s.active_devices}  l="Active"     bg={C.successBg} c={C.successText} />
                        <Tile v={s.renewable_count} l="Renewable"  bg="#F5F3FF" c="#5B21B6" />
                        <Tile v={s.overdue_count}   l="Overdue"    bg={s.overdue_count ? '#FEE2E2' : '#F3F4F6'} c={s.overdue_count ? '#991B1B' : '#9CA3AF'} />
                        <Tile v={s.due_soon_count}  l="Due Soon"   bg={s.due_soon_count ? '#FEF3C7' : '#F3F4F6'} c={s.due_soon_count ? '#92400E' : '#9CA3AF'} />
                        <Tile v={s.cancelled_count} l="Cancelled"  bg={s.cancelled_count ? '#FEE2E2' : '#F3F4F6'} c={s.cancelled_count ? '#991B1B' : '#9CA3AF'} />
                      </div>
                    </Panel>

                    <Panel title="Activity" right={<span style={{ fontSize: '11px', color: C.textSub }}>{data.timeline.length} events</span>}>
                      {data.timeline.length === 0 ? <SmallEmpty text="No activity yet." /> : (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          {data.timeline.map((t, i) => (
                            <div key={i} style={{ display: 'flex', gap: '10px', paddingBottom: i === data.timeline.length - 1 ? 0 : '12px' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: `${t.color}1A`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>
                                  {t.icon}
                                </div>
                                {i !== data.timeline.length - 1 && <div style={{ width: '2px', flex: 1, background: C.border, marginTop: '3px' }} />}
                              </div>
                              <div style={{ minWidth: 0, paddingBottom: '2px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, lineHeight: 1.4 }}>{t.title}</div>
                                {t.detail && <div style={{ fontSize: '11px', color: C.textSub }}>{t.detail}</div>}
                                <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '1px' }}>
                                  {t.date}{t.time ? ` · ${t.time}` : ''}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </Panel>
                  </div>
                </div>
              )}

              {/* ── DEVICES ── */}
              {tab === 'devices' && (
                <>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
                    <input value={devQ} onChange={e => setDevQ(e.target.value)}
                      placeholder="Search serial, ref no, category…"
                      style={{ flex: 1, minWidth: '220px', padding: '9px 14px', border: `1.5px solid ${C.border}`, borderRadius: '9px', fontSize: '13px', outline: 'none' }} />
                    <span style={{ fontSize: '12px', color: C.textSub }}>{devices.length} of {data.devices.length}</span>
                  </div>

                  {devices.length === 0 ? <Empty icon="📦" text={devQ ? 'No devices match your search.' : 'No devices mapped to this client yet.'} />
                  : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '14px' }}>
                      {devices.map(d => {
                        const st = STATUS_STYLE[d.status] || { bg: '#F3F4F6', color: '#374151', label: d.status, icon: '📟' };
                        const r  = d.renewal_status ? RENEW[d.renewal_status] : null;
                        const w  = d.warranty_status ? WARRANTY[d.warranty_status] : null;
                        return (
                          <div key={`${d.mapping_db_id}-${d.inv_db_id}`} style={{
                            border: `1px solid ${d.cancelled ? '#FECACA' : C.border}`, borderRadius: '14px',
                            background: C.white, overflow: 'hidden',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                          }}>
                            {/* head */}
                            <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start' }}>
                              <div style={{ display: 'flex', gap: '11px', minWidth: 0 }}>
                                <div style={{ width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0, background: st.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                                  {st.icon}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: '14px', fontWeight: 800, color: C.textMain }}>{d.category_name}</div>
                                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '3px' }}>
                                    {d.type_name && <Pill bg={C.primaryBg} color={C.primaryDeep}>{d.type_name}</Pill>}
                                    <Pill bg={st.bg} color={st.color}>{st.label}</Pill>
                                  </div>
                                </div>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <div style={{ fontSize: '17px', fontWeight: 900, color: C.primaryDeep }}>{money(d.unit_amount)}</div>
                                {d.amc_amount > 0 && <div style={{ fontSize: '11px', fontWeight: 700, color: '#7C3AED' }}>AMC {money(d.amc_amount)}</div>}
                              </div>
                            </div>

                            {/* identifiers */}
                            <div style={{ padding: '0 16px 12px' }}>
                              <div style={{ background: '#FAFAFB', border: `1px solid ${C.border}`, borderRadius: '9px', padding: '9px 12px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <CopyRow label="Serial No" value={d.serial_number} />
                                {d.ref_number && <CopyRow label="Ref No" value={d.ref_number} />}
                                <CopyRow label="Item ID" value={d.item_id} />
                              </div>
                            </div>

                            {/* renewal */}
                            {d.is_renewable && (
                              <div style={{ padding: '0 16px 12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                  <span style={{ fontSize: '11px', fontWeight: 800, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                    Renewal · {d.duration_months}mo term
                                  </span>
                                  {r && <Pill bg={r.bg} color={r.color}>{r.label} · {daysText(d.days_remaining)}</Pill>}
                                </div>
                                <Bar pct={d.term_progress} color={(r || RENEW.upcoming).bar} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px' }}>
                                  <span style={{ fontSize: '11px', color: C.textSub }}>Installed {d.install_date}</span>
                                  <span style={{ fontSize: '11px', fontWeight: 800, color: C.textMain }}>Due {d.due_date}</span>
                                </div>
                                {d.renewed_times > 0 && (
                                  <div style={{ marginTop: '6px' }}>
                                    <Pill bg="#D1FAE5" color="#065F46">↻ Renewed {d.renewed_times}× · +{d.renewed_months} months added</Pill>
                                  </div>
                                )}
                              </div>
                            )}

                            {d.cancelled && (
                              <div style={{ margin: '0 16px 12px', padding: '9px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '9px', fontSize: '11px', color: '#991B1B', fontWeight: 600 }}>
                                🚫 Subscription cancelled{d.cancel_reason ? ` — "${d.cancel_reason}"` : ''}
                              </div>
                            )}

                            {/* replacements */}
                            {d.replacements.length > 0 && (
                              <div style={{ margin: '0 16px 12px', padding: '9px 12px', background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: '9px' }}>
                                <div style={{ fontSize: '11px', fontWeight: 800, color: '#0369A1', marginBottom: '5px' }}>🔄 Replacement history</div>
                                {d.replacements.map(rp => (
                                  <div key={rp.id} style={{ fontSize: '11px', color: '#0C4A6E', lineHeight: 1.6 }}>
                                    <span style={{ fontFamily: 'monospace' }}>{rp.old_serial || rp.old_item || '—'}</span>
                                    {' → '}
                                    <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{rp.new_serial || rp.new_item || '—'}</span>
                                    <span style={{ color: '#0EA5E9' }}> · {rp.reason} · {rp.replaced_at}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* foot */}
                            <div style={{ padding: '10px 16px', background: '#FAFAFB', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                              {w && <Pill bg={w.bg} color={w.color}>🛡️ {w.label}{d.warranty_end ? ` · ${d.warranty_end}` : ''}</Pill>}
                              {d.installer_name && <span style={{ fontSize: '11px', color: C.textSub }}>👷 {d.installer_name}</span>}
                              <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: '10px', color: C.textSub }}>
                                {d.mapping_ref} · {d.package_ref}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* ── RENEWALS ── */}
              {tab === 'renewals' && (
                data.renewals.length === 0 ? <Empty icon="🔁" text="No renewals completed for this client yet." />
                : (
                  <Table cols={['Ref', 'Device', 'Period Covered', 'Term', 'Source', 'Renewed On']}>
                    {data.renewals.map((r, i) => (
                      <tr key={r.id} className="cpd-row" style={{ background: i % 2 === 0 ? C.white : '#FAFAFA', borderBottom: `1px solid ${C.border}` }}>
                        <td style={tdS}>
                          <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 800, color: '#5B21B6', background: '#EDE9FE', padding: '2px 8px', borderRadius: '6px' }}>{r.ref || '—'}</span>
                        </td>
                        <td style={tdS}>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain }}>{r.category_name} {r.type_name && <span style={{ color: C.textSub }}>· {r.type_name}</span>}</div>
                          <div style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textSub }}>{r.serial_number}</div>
                        </td>
                        <td style={tdS}>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: '#5B21B6' }}>{r.period_from} → {r.period_to}</span>
                        </td>
                        <td style={tdS}><Pill bg="#EDE9FE" color="#5B21B6">+{r.months} mo</Pill></td>
                        <td style={tdS}>
                          {r.source === 'dealer_request' ? <Pill bg="#F0FDFA" color="#0F766E">🏪 Dealer request</Pill>
                            : r.source === 'restore' ? <Pill bg="#DBEAFE" color="#1D4ED8">↩ Restored</Pill>
                            : <Pill bg="#EDE9FE" color="#5B21B6">🧑‍💼 Admin</Pill>}
                        </td>
                        <td style={{ ...tdS, color: C.textSub, whiteSpace: 'nowrap' }}>{r.renewed_at}</td>
                      </tr>
                    ))}
                  </Table>
                )
              )}

              {/* ── PAYMENTS ── */}
              {tab === 'payments' && (
                data.payments.length === 0 ? <Empty icon="💳" text="No payments recorded yet." />
                : (
                  <Table cols={['Receipt', 'Against', 'Mode', 'Method', 'Paid', 'Balance After', 'Date']}>
                    {data.payments.map((p, i) => (
                      <tr key={p.id} className="cpd-row" style={{ background: i % 2 === 0 ? C.white : '#FAFAFA', borderBottom: `1px solid ${C.border}` }}>
                        <td style={tdS}>
                          <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 800, color: '#5B21B6', background: '#EDE9FE', padding: '2px 8px', borderRadius: '6px' }}>{p.payment_id}</span>
                        </td>
                        <td style={tdS}>
                          <div style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textMain }}>{p.mapping_ref}</div>
                          <div style={{ fontSize: '10px', color: C.textSub }}>{p.entry_type === 'renewal' ? 'AMC renewal' : 'Product'}</div>
                        </td>
                        <td style={tdS}>
                          <Pill bg={p.payment_mode === 'online' ? '#EDE9FE' : C.warningBg} color={p.payment_mode === 'online' ? '#5B21B6' : C.warningText}>
                            {p.payment_mode === 'online' ? '🌐 Online' : '💵 Offline'}
                          </Pill>
                        </td>
                        <td style={{ ...tdS, fontWeight: 600 }}>{p.payment_method || '—'}</td>
                        <td style={{ ...tdS, fontWeight: 900, color: C.successText, fontSize: '13px' }}>{money(p.amount_paid)}</td>
                        <td style={tdS}>
                          {p.balance_due > 0.01
                            ? <span style={{ fontWeight: 700, color: C.warningText }}>{money(p.balance_due)}</span>
                            : <Pill bg={C.successBg} color={C.successText}>✓ Cleared</Pill>}
                        </td>
                        <td style={{ ...tdS, color: C.textSub, whiteSpace: 'nowrap' }}>{p.created_at}</td>
                      </tr>
                    ))}
                  </Table>
                )
              )}

              {/* ── ORDERS ── */}
              {tab === 'orders' && (
                data.orders.length === 0 ? <Empty icon="🧾" text="No orders for this client." />
                : (
                  <Table cols={['Reference', 'Type', 'Status', 'Billed', 'Paid', 'Balance', 'Date']}>
                    {data.orders.map((o, i) => (
                      <tr key={o.id} className="cpd-row" style={{ background: i % 2 === 0 ? C.white : '#FAFAFA', borderBottom: `1px solid ${C.border}` }}>
                        <td style={tdS}>
                          <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 800, color: C.primaryDeep }}>{o.mapping_ref}</div>
                          {o.package_ref && <div style={{ fontFamily: 'monospace', fontSize: '10px', color: C.textSub }}>{o.package_ref}</div>}
                        </td>
                        <td style={tdS}>
                          {o.entry_type === 'renewal' ? <Pill bg="#F5F3FF" color="#5B21B6">🔁 Renewal</Pill> : <Pill bg={C.primaryBg} color={C.primaryDeep}>📦 Product</Pill>}
                        </td>
                        <td style={tdS}>
                          <Pill bg={o.status === 'completed' ? C.successBg : o.status === 'cancelled' ? C.dangerBg : C.warningBg}
                                color={o.status === 'completed' ? C.successText : o.status === 'cancelled' ? C.dangerText : C.warningText}>
                            {o.status}
                          </Pill>
                        </td>
                        <td style={{ ...tdS, fontWeight: 700 }}>{money(o.total_due)}</td>
                        <td style={{ ...tdS, fontWeight: 700, color: C.successText }}>{money(o.paid)}</td>
                        <td style={tdS}>
                          {o.balance > 0.01 ? <span style={{ fontWeight: 800, color: C.dangerText }}>{money(o.balance)}</span> : <Pill bg={C.successBg} color={C.successText}>✓ Cleared</Pill>}
                        </td>
                        <td style={{ ...tdS, color: C.textSub, whiteSpace: 'nowrap' }}>{o.created_at}</td>
                      </tr>
                    ))}
                  </Table>
                )
              )}

              {/* ── SUPPORT ── */}
              {tab === 'support' && (
                data.support.length === 0 ? <Empty icon="🛠️" text="No service requests or complaints for this client." />
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {data.support.map(t => {
                      const st = SUPPORT[t.status] || { bg: '#F3F4F6', color: '#374151', label: t.status };
                      return (
                        <div key={`${t.kind}-${t.id}`} style={{ border: `1px solid ${C.border}`, borderRadius: '12px', padding: '13px 16px', background: C.white, display: 'flex', gap: '12px' }}>
                          <div style={{ width: '36px', height: '36px', borderRadius: '9px', flexShrink: 0, background: t.kind === 'service' ? '#FEF3C7' : '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                            {t.kind === 'service' ? '🛠️' : '⚠️'}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '13px', fontWeight: 700, color: C.textMain }}>
                                {t.kind === 'service' ? 'Service Request' : 'Complaint'}
                              </span>
                              <span style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textSub }}>{t.ref}</span>
                              <Pill bg={st.bg} color={st.color}>{st.label}</Pill>
                            </div>
                            <div style={{ fontSize: '12px', color: C.textMain, marginTop: '4px', lineHeight: 1.5 }}>{t.text}</div>
                            {t.notes && <div style={{ fontSize: '11px', color: C.textSub, fontStyle: 'italic', marginTop: '3px' }}>"{t.notes}"</div>}
                            <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '4px' }}>
                              Raised {t.created_at}{t.resolved_at ? ` · Resolved ${t.resolved_at}` : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

/* ── bits ─────────────────────────────────────────────────────────────────── */

function Panel({ title, right, children }) {
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '14px', overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', background: '#FCFCFD' }}>
        <span style={{ fontSize: '12px', fontWeight: 800, color: C.textMain, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</span>
        {right}
      </div>
      <div style={{ padding: '14px 16px' }}>{children}</div>
    </div>
  );
}

function Bar({ pct, color }) {
  return (
    <div style={{ height: '7px', background: '#F3F4F6', borderRadius: '6px', overflow: 'hidden' }}>
      <div style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%`, height: '100%', background: color, borderRadius: '6px', transition: 'width 0.4s ease' }} />
    </div>
  );
}

function Tile({ v, l, bg, c }) {
  return (
    <div style={{ background: bg, borderRadius: '10px', padding: '10px 12px' }}>
      <div style={{ fontSize: '20px', fontWeight: 900, color: c, lineHeight: 1.1 }}>{v}</div>
      <div style={{ fontSize: '10px', fontWeight: 700, color: c, opacity: 0.85, marginTop: '2px' }}>{l}</div>
    </div>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div style={{ background: '#FAFAFB', border: `1px solid ${C.border}`, borderRadius: '9px', padding: '9px 12px' }}>
      <div style={{ fontSize: '10px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
      <div style={{ fontSize: '15px', fontWeight: 900, color: C.textMain, marginTop: '2px' }}>{value}</div>
      {sub && <div style={{ fontSize: '10px', color: C.textSub }}>{sub}</div>}
    </div>
  );
}

function Pill({ children, bg, color }) {
  return (
    <span style={{ background: bg, color, padding: '2px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

function Meta({ icon, value, copy }) {
  const [done, setDone] = useState(false);
  const onCopy = () => {
    if (!copy) return;
    navigator.clipboard?.writeText(value);
    setDone(true);
    setTimeout(() => setDone(false), 1200);
  };
  return (
    <span onClick={onCopy} title={copy ? 'Click to copy' : undefined}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: C.textSub, cursor: copy ? 'pointer' : 'default' }}>
      <span>{icon}</span>{value}
      {copy && <span style={{ fontSize: '10px', color: done ? C.success : '#D1D5DB' }}>{done ? '✓' : '⧉'}</span>}
    </span>
  );
}

function CopyRow({ label, value }) {
  const [done, setDone] = useState(false);
  const onCopy = () => {
    navigator.clipboard?.writeText(value || '');
    setDone(true);
    setTimeout(() => setDone(false), 1200);
  };
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
      <span style={{ fontSize: '10px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.4px', flexShrink: 0 }}>{label}</span>
      <span onClick={onCopy} title="Click to copy"
        style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: C.textMain, cursor: 'pointer', textAlign: 'right', wordBreak: 'break-all', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
        {value || '—'}
        <span style={{ fontSize: '10px', color: done ? C.success : '#D1D5DB', flexShrink: 0 }}>{done ? '✓' : '⧉'}</span>
      </span>
    </div>
  );
}

function Empty({ icon, text }) {
  return (
    <div style={{ textAlign: 'center', padding: '70px 20px' }}>
      <div style={{ fontSize: '40px', marginBottom: '10px' }}>{icon}</div>
      <div style={{ fontSize: '13px', color: C.textSub }}>{text}</div>
    </div>
  );
}

function SmallEmpty({ text }) {
  return <div style={{ padding: '18px', textAlign: 'center', fontSize: '12px', color: C.textSub }}>{text}</div>;
}

function Center({ children }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <div style={{
      width: '30px', height: '30px', border: `3px solid ${C.border}`,
      borderTopColor: C.primary, borderRadius: '50%', animation: 'spin 0.8s linear infinite',
    }} />
  );
}

function Table({ cols, children }) {
  return (
    <div style={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: '14px', background: C.white }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
        <thead>
          <tr style={{ background: C.primaryBg }}>
            {cols.map(h => (
              <th key={h} style={{ padding: '11px 14px', fontSize: '10px', fontWeight: 800, color: C.textSub, textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap' }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

const tdS = { padding: '12px 14px', fontSize: '12px', verticalAlign: 'middle', color: C.textMain };
const ghostBtn = {
  padding: '8px 18px', background: C.primaryBg, color: C.textSub,
  border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
};
